import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  InjectionToken,
  Input,
  OnInit,
  Optional,
  ViewEncapsulation
} from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { AutoUnsubscribe } from '@nitro/core/common';
import { NkPickerControl } from '@nitro/reactor/common';

import { DateTimeAdapter } from '../adapters/date-time-adapter';

export interface NkTimeConfig {
  clamp?: boolean;
}
export const NK_TIME_DEFAULT_OPTIONS = new InjectionToken<NkTimeConfig>('NK_TIME_DEFAULT_OPTIONS');

@AutoUnsubscribe()
@Component({
  selector: 'nk-datetimeedit-base',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class NkDateTimeBase<D> extends NkPickerControl<D> implements OnInit {
  _value: D | null;
  private _minDate: D | null;
  private _maxDate: D | null;

  @Input()
  get value(): D | null {
    return this._value;
  }
  set value(v: D | null) {
    if (v === null || !this._dtAdapter.sameDateTime(v, this._value)) {
      this._value = v;
      this.valueChange.emit(v);
      this.updateControls(v);
      this._changeDetectorRef.markForCheck();
    }
  }

  _disabled: boolean;
  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(d: boolean) {
    this._disabled = d;
  }

  /** The maximum selectable date. */
  @Input()
  get max(): D | null {
    return this._maxDate;
  }
  set max(value: D | null) {
    const newMax = this._dtAdapter.validOrNull(this._dtAdapter.deserialize(value));
    if (!this._dtAdapter.sameDateTime(newMax, this._maxDate)) {
      this._maxDate = newMax;
      this._onValidatorChange();
    }
  }

  @Input()
  get min(): D | null {
    return this._minDate;
  }
  set min(value: D | null) {
    const newMin = this._dtAdapter.validOrNull(this._dtAdapter.deserialize(value));
    if (!this._dtAdapter.sameDateTime(newMin, this._minDate)) {
      this._minDate = newMin;
      this._onValidatorChange();
    }
  }

  @Input() showMeridian;
  @Input() showMilliseconds;
  @Input() showSeconds;
  @Input() clamp;

  private _minValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const controlValue = this._dtAdapter.validOrNull(this._dtAdapter.deserialize(control.value));
    return !this.min ||
      !controlValue ||
      this._dtAdapter.compareDateTime(this.min, controlValue) <= 0
      ? null
      : { nkTimeEditMin: { min: this.min, actual: controlValue } };
  };

  /** The form control validator for the max date. */
  private _maxValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const controlValue = this._dtAdapter.validOrNull(this._dtAdapter.deserialize(control.value));
    return !this.max ||
      !controlValue ||
      this._dtAdapter.compareDateTime(this.max, controlValue) >= 0
      ? null
      : { nkTimeEditMax: { max: this.max, actual: controlValue } };
  };

  validator: ValidatorFn | null = Validators.compose([
    // eslint-disable-line
    this._minValidator,
    this._maxValidator
  ]);

  constructor(
    public _dtAdapter: DateTimeAdapter<D>,
    @Optional() @Inject(NK_TIME_DEFAULT_OPTIONS) public config: NkTimeConfig,
    public _changeDetectorRef: ChangeDetectorRef
  ) {
    super();
    this.parser = this._dtAdapter.parse;
    this.formatter = this._dtAdapter.format;
  }

  setMeridian(pm: boolean) {
    if (this.value) {
      this.value = this._dtAdapter.setMeridian(this._value, pm);
      this._onChange(this.value);
      this._onTouched();
      this.userSelection.emit(this.value);
    }
  }

  setTime(hh: string | number, mm: string | number, ss: string | number, ms: string | number) {
    const dt = this._dtAdapter.setTime(this.value, hh, mm, ss, ms);
    const newV = this.clamp ? this._dtAdapter.clampDate(dt, this.min, this.max) : dt;
    if (!this._dtAdapter.sameDateTime(newV, this.value)) {
      this.value = newV;
    }
  }

  ngOnInit(): void {
    this.config = this.config || {};
    this.clamp = coerceBooleanProperty(this.clamp || this.config.clamp);
  }
}
